/**
 * Single calendar grid component: day names row + 7-column grid of date cells.
 * Layout matches docs/calendarFlow (circular cells, compact weekday row); theme from store.
 * Date-cell colors/icons come from `calendarDateCellVisuals` via pre-resolved `cell.visual`.
 */
import React, { useMemo, memo } from 'react';
import { View, Text, StyleSheet, Animated, ViewStyle, useWindowDimensions } from 'react-native';
import { SvgIcon } from '../../components/common/SvgIcon';
import { Clickable } from '../../components/common/Clickable';
import { appTheme, useThemeColors } from '../../shared/theme';
import type { ThemeColors } from '../../shared/theme/store';
import { formatNumber, layoutLtrStyle } from '../../shared/i18n';
import {
  CALENDAR_GRID_GAP,
  CALENDAR_CELL_SIZE_MIN,
  CALENDAR_CELL_BORDER_RADIUS,
  CALENDAR_CELL_ICON_SIZE,
  CALENDAR_GRID_ANIMATION_INSET,
  CALENDAR_ANDROID_TEXT_COMPACT,
  computeCalendarCellSize,
} from '../../data/domains/calendar';
import type { CalendarDay } from '../../data/domains/calendar';
import type { CalendarDateCellVisual } from './calendarDateCellVisuals';
import { CalendarDateCellAnimation } from './CalendarDateCellAnimation';

const DAY_NAMES_DEFAULT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface CalendarGridCellData {
  day: CalendarDay;
  index: number;
  visual: CalendarDateCellVisual;
  disabled: boolean;
  /**
   * 1 = one column; 2 = width of two cells + gap (pair omitted from list).
   * Double-box anchors in the same week must use 2 — see buildCalendarGridCells.ts
   * and `.cursor/rules/calendar-double-box-span.mdc` (do not regress to single-cell ×2).
   */
  col_span?: 1 | 2;
  /** Show double-box treatment (`D–D+1 ×2` when paired). */
  is_double_box_span?: boolean;
  /** Paired calendar day-of-month when spanning (required for correct two-date label). */
  double_box_pair_day?: number | null;
  double_box_pair_date?: string;
}

export interface CalendarGridProps {
  day_names?: string[];
  cells: CalendarGridCellData[];
  on_date_press: (day: CalendarDay, index: number) => void;
  slide_anim?: Animated.Value;
  contentContainerStyle?: ViewStyle;
  /** Precomputed cell width from the screen — avoids per-grid dimension subscriptions. */
  cell_size?: number;
  /** Stable theme fingerprint for style memoization. */
  theme_style_key?: string;
  /** Pause looping cell animations while the parent scroll view is moving. */
  suppress_cell_animations?: boolean;
  /** set2 uses v2 cell chrome (rounded cells, compact weekday row, no motion). */
  grid_variant?: 'default' | 'set2';
}

function createGridStyles(c: ThemeColors, variant: 'default' | 'set2') {
  const is_set2 = variant === 'set2';
  return StyleSheet.create({
    day_names_container: {
      alignSelf: 'center',
      flexDirection: 'row',
      marginBottom: 8,
      paddingVertical: 4,
      paddingHorizontal: 0,
    },
    day_name: {
      textAlign: 'center',
      fontWeight: is_set2 ? '500' : '800',
      color: is_set2 ? c.textLight : c.textSecondary,
      fontSize: 11,
      letterSpacing: is_set2 ? 0.3 : 0.5,
      ...(is_set2 ? { textTransform: 'uppercase' as const } : null),
    },
    day_names_row_inner: {
      flexDirection: 'row',
      gap: CALENDAR_GRID_GAP,
      justifyContent: 'flex-start',
    },
    grid: {
      alignSelf: 'center',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: CALENDAR_GRID_GAP,
      justifyContent: 'flex-start',
      alignContent: 'flex-start',
      overflow: 'visible',
      paddingVertical: CALENDAR_GRID_ANIMATION_INSET,
    },
    gridLtr: { ...layoutLtrStyle },
    date_cell: {
      justifyContent: 'center',
      alignItems: 'center',
      padding: 0,
      borderRadius: is_set2 ? 999 : CALENDAR_CELL_BORDER_RADIUS,
      overflow: 'hidden',
      alignSelf: 'center',
    },
    date_cell_inner: {
      width: '100%',
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: is_set2 ? 999 : CALENDAR_CELL_BORDER_RADIUS,
      padding: is_set2 ? 4 : undefined,
      paddingVertical: is_set2 ? undefined : 3,
      paddingHorizontal: is_set2 ? undefined : 2,
    },
    date_number: {
      fontSize: 13,
      lineHeight: 16,
      fontWeight: is_set2 ? '400' : '500',
      color: c.text,
      textAlign: 'center',
      ...CALENDAR_ANDROID_TEXT_COMPACT,
    },
    double_box_label_row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      maxWidth: '100%',
    },
    double_box_x2: {
      fontSize: 11,
      lineHeight: 14,
      fontWeight: '800',
      letterSpacing: 0.15,
      ...CALENDAR_ANDROID_TEXT_COMPACT,
    },
    date_cell_span2: {
      borderRadius: is_set2 ? CALENDAR_CELL_BORDER_RADIUS : CALENDAR_CELL_BORDER_RADIUS,
    },
    cell_touch_slot: {
      overflow: 'visible',
      alignItems: 'center',
      justifyContent: 'center',
    },
    cell_touch_slot_set2: {
      borderRadius: 999,
      overflow: 'hidden',
    },
    cell_touch_slot_span2: {
      overflow: 'hidden',
      borderRadius: CALENDAR_CELL_BORDER_RADIUS,
    },
    status_icon_slot: {
      height: CALENDAR_CELL_ICON_SIZE + 2,
      marginTop: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
  });
}

type GridStyles = ReturnType<typeof createGridStyles>;

const DateCell = memo(function DateCell({
  cell,
  cell_size,
  on_press,
  gridStyles: styles,
  suppress_cell_animations,
  default_icon_size,
  is_set2,
}: {
  cell: CalendarGridCellData;
  cell_size: number;
  on_press: (day: CalendarDay, index: number) => void;
  gridStyles: GridStyles;
  suppress_cell_animations?: boolean;
  default_icon_size: number;
  is_set2: boolean;
}) {
  const { day, index, visual, disabled, col_span = 1, is_double_box_span, double_box_pair_day } = cell;
  const span = col_span === 2 ? 2 : 1;
  const width = span === 2 ? cell_size * 2 + CALENDAR_GRID_GAP : cell_size;
  const size_style = { width, height: cell_size };

  if (day.date === null) {
    return <View style={[styles.date_cell, size_style]} />;
  }

  const date_label =
    is_double_box_span && day.date != null ? (
      <View style={styles.double_box_label_row}>
        <Text style={[styles.date_number, visual.textStyle]}>
          {double_box_pair_day != null
            ? `${formatNumber(day.date)}–${formatNumber(double_box_pair_day)}`
            : formatNumber(day.date)}
        </Text>
        <Text style={[styles.double_box_x2, visual.textStyle]}>×{formatNumber(2)}</Text>
      </View>
    ) : (
      <Text style={[styles.date_number, visual.textStyle]}>{day.date != null ? formatNumber(day.date) : ''}</Text>
    );

  const cell_content = (
    <View style={[styles.date_cell, span === 2 && styles.date_cell_span2, size_style, ...visual.cellStyles]}>
      <View style={styles.date_cell_inner}>
        {date_label}
        <View style={styles.status_icon_slot}>
          {visual.icon ? (
            <SvgIcon name={visual.icon.name} size={visual.icon.size ? visual.icon.size : default_icon_size} color={visual.icon.color} />
          ) : null}
        </View>
      </View>
    </View>
  );

  const wrapped_content = visual.animation ? (
    <CalendarDateCellAnimation preset={visual.animation} suppressed={suppress_cell_animations}>
      {cell_content}
    </CalendarDateCellAnimation>
  ) : (
    cell_content
  );

  const a11y =
    is_double_box_span && day.date != null
      ? `Date ${day.date} double box`
      : day.fullDate
        ? `Date ${day.date}`
        : undefined;

  return (
    <Clickable
      onPress={() => on_press(day, index)}
      disabled={disabled}
      pressAnimated={false}
      haptics
      accessibilityLabel={a11y}
      accessibilityRole="button"
      style={[
        styles.cell_touch_slot,
        is_set2 && styles.cell_touch_slot_set2,
        span === 2 && styles.cell_touch_slot_span2,
        size_style,
      ]}>
      {wrapped_content}
    </Clickable>
  );
});

function calendarGridPropsAreEqual(prev: CalendarGridProps, next: CalendarGridProps): boolean {
  if (prev.cell_size !== next.cell_size) {
    return false;
  }
  if (prev.theme_style_key !== next.theme_style_key) {
    return false;
  }
  if (prev.suppress_cell_animations !== next.suppress_cell_animations) {
    return false;
  }
  if (prev.grid_variant !== next.grid_variant) {
    return false;
  }
  if (prev.day_names !== next.day_names) {
    return false;
  }
  if (prev.slide_anim !== next.slide_anim) {
    return false;
  }
  if (prev.contentContainerStyle !== next.contentContainerStyle) {
    return false;
  }
  if (prev.on_date_press !== next.on_date_press) {
    return false;
  }
  if (prev.cells === next.cells) {
    return true;
  }
  if (prev.cells.length !== next.cells.length) {
    return false;
  }
  for (let i = 0; i < prev.cells.length; i++) {
    const a = prev.cells[i];
    const b = next.cells[i];
    if (a.day !== b.day) {
      return false;
    }
    if (a.disabled !== b.disabled) {
      return false;
    }
    if ((a.col_span ?? 1) !== (b.col_span ?? 1) || Boolean(a.is_double_box_span) !== Boolean(b.is_double_box_span)) {
      return false;
    }
    if ((a.double_box_pair_day ?? null) !== (b.double_box_pair_day ?? null)) {
      return false;
    }
    const av = a.visual;
    const bv = b.visual;
    if ((av.icon?.name ?? '') !== (bv.icon?.name ?? '') || (av.icon?.color ?? '') !== (bv.icon?.color ?? '')) {
      return false;
    }
    if (av.animation !== bv.animation) {
      return false;
    }
    if ((av.textStyle?.color ?? '') !== (bv.textStyle?.color ?? '') || av.textStyle?.fontWeight !== bv.textStyle?.fontWeight) {
      return false;
    }
    if (av.cellStyles.length !== bv.cellStyles.length) {
      return false;
    }
    for (let j = 0; j < av.cellStyles.length; j++) {
      const ac = av.cellStyles[j];
      const bc = bv.cellStyles[j];
      if (ac.backgroundColor !== bc.backgroundColor || ac.opacity !== bc.opacity || ac.borderColor !== bc.borderColor) {
        return false;
      }
    }
  }
  return true;
}

export const CalendarGrid = memo(function CalendarGridInner(props: CalendarGridProps): React.ReactElement {
  const {
    day_names = DAY_NAMES_DEFAULT,
    cells,
    on_date_press,
    slide_anim,
    contentContainerStyle,
    cell_size: cell_size_prop,
    theme_style_key,
    suppress_cell_animations,
    grid_variant = 'default',
  } = props;
  const colors = useThemeColors();
  const palette = colors ?? appTheme.colors;
  const gridStyles = useMemo(() => createGridStyles(palette, grid_variant), [theme_style_key, palette, grid_variant]);
  const default_icon_size = grid_variant === 'set2' ? 14 : CALENDAR_CELL_ICON_SIZE;
  const { width } = useWindowDimensions();
  const cell_size = useMemo(
    () => (cell_size_prop != null ? cell_size_prop : computeCalendarCellSize(width)),
    [cell_size_prop, width],
  );
  const grid_width = 7 * cell_size + 6 * CALENDAR_GRID_GAP;

  const day_names_row = (
    <View style={[gridStyles.day_names_container, gridStyles.gridLtr, { width: grid_width }]}>
      <View style={[gridStyles.day_names_row_inner, { width: grid_width }]}>
        {day_names.map((day, index) => (
          <Text key={`day-${index}`} style={[gridStyles.day_name, { width: cell_size }]}>
            {day}
          </Text>
        ))}
      </View>
    </View>
  );

  const grid_content = (
    <View style={[gridStyles.grid, gridStyles.gridLtr, { width: grid_width }]}>
      {cells.map((cell, idx) => (
        <DateCell
          key={cell.day.fullDate || `empty-${idx}`}
          cell={cell}
          cell_size={cell_size}
          on_press={on_date_press}
          gridStyles={gridStyles}
          suppress_cell_animations={suppress_cell_animations || grid_variant === 'set2'}
          default_icon_size={default_icon_size}
          is_set2={grid_variant === 'set2'}
        />
      ))}
    </View>
  );

  return (
    <View style={contentContainerStyle}>
      {day_names_row}
      {slide_anim != null ? <Animated.View style={{ transform: [{ translateX: slide_anim }] }}>{grid_content}</Animated.View> : grid_content}
    </View>
  );
}, calendarGridPropsAreEqual);
